import {Component, Inject, Input, OnInit, PLATFORM_ID} from '@angular/core';
import {LeagueHttpService} from '@api/http/league/league-http.service';
import {AuthDialogService} from '@modules/dialog/services/auth-dialog.service';
import {UserService} from '@core/services/user.service';
import {LinkService} from '@core/services/link.service';
import {OverlayService} from '@lib-modules/overlays/overlay.service';
import {AppPromotionComponent} from '@shared/components/promotions/app-promotion/app-promotion.component';
import {MS} from '@utils/core/time';
import {isPlatformBrowser} from '@angular/common';
import {StateService} from '@core/services/state.service';
import {User} from '@shared/models/user';
import {Team} from '@api/models/team/team';
import {League} from '@api/models/league/league';
import {TeamService} from '@core/services/team.service';
import {LocalStorageKey} from '@utils/storage/storage-keys';
import {EntityType} from '@lib/models/entity/entity-type';
import {FollowService} from '@core/services/follow/follow.service';

interface PromoImageContainer {
  followImage: string;
  linkImage: string;
}

const DEFAULT_ENTITY = 'bundesliga';
const DEFAULT_ENTITY_IMAGES: PromoImageContainer = {
  linkImage: 'https://static.fan.at/website/images/bundesliga-linked.png',
  followImage: 'https://static.fan.at/website/images/bundesliga-banner.png'
}

@Component({
  selector: 'app-follow-banner',
  templateUrl: './follow-banner.component.html',
  styleUrls: ['./follow-banner.component.scss']
})
export class FollowBannerComponent implements OnInit {
  @Input() entityReadableId = DEFAULT_ENTITY;
  public entityId: string;
  entityName: string;
  entityType = EntityType.League;
  entity: Team | League;
  entityHasPromoImage = false;
  entityPromoImages: PromoImageContainer = null;
  public userFollowsEntity = false;
  public userLoggedIn = false;
  public userDismissedLogin = false;
  public loading = true;
  public entityLink: string;
  public displayedInWebView = StateService.isWebview;

  constructor(
    private authDialogService: AuthDialogService,
    private followService: FollowService,
    private userService: UserService,
    private leagueService: LeagueHttpService,
    private teamService: TeamService,
    private overlayService: OverlayService,
    @Inject(PLATFORM_ID) protected platformId: Object,
  ) { }

  async ngOnInit(): Promise<void> {
    // for now, hardcoded, should be provided by BE in the future
    if (this.entityReadableId === DEFAULT_ENTITY) {
      this.entityHasPromoImage = true;
      this.entityPromoImages = DEFAULT_ENTITY_IMAGES;
    }

    if (!isPlatformBrowser(this.platformId)) {
      this.userFollowsEntity = true;
      this.loading = false;
      return;
    }

    if (this.entityType === EntityType.League) {
      const response = await this.leagueService.getLeagueByReadableId(this.entityReadableId);
      this.entityId = response.data.objectid;
      this.entityName = response.data.object.name;
      this.entity = response.data.object;
    } else if (this.entityType === EntityType.Team) {
      const response = await this.teamService.getTeamWithRid(this.entityReadableId);
      this.entityId = response._id;
      this.entityName = response.name;
      this.entity = response;
    }

    this.userFollowsEntity = this.followService.userFollows(this.entityType, this.entityId);

    this.entityLink = LinkService.getLinkForSubdomain(this.entityReadableId);

    const userDismissedPopupBefore = +localStorage.getItem(LocalStorageKey.FollowPopupDismissed);
    this.userDismissedLogin = !isNaN(userDismissedPopupBefore) && Date.now() - userDismissedPopupBefore < MS.WEEK;

    this.loading = false;
  }

  private async updateLoginState(): Promise<User> {
    const user = await this.userService.getCurrentUser();
    this.userLoggedIn = !!user;
    return user;
  }

  async followButtonClick() {
    await this.updateLoginState();

    if (this.userLoggedIn) {
      await this.followEntity();
    } else {
      await this.handleUnregisteredUser();
    }
  }

  private async handleUnregisteredUser() {
    if (this.displayedInWebView) {
      return;
    }

    const overlayRef = await this.overlayService.open(AppPromotionComponent, undefined, undefined, true);
    overlayRef.onClosed().subscribe(async () => {

      const login = await this.authDialogService.openLogin();

      if (!login) {
        this.userDismissedLogin = true;
        localStorage.setItem(LocalStorageKey.FollowPopupDismissed, Date.now().toString());
        return;
      } else {
        await this.followEntity()
      }
    })
  }
  private async followEntity() {
    try {
      this.userLoggedIn = true;
      this.userFollowsEntity = await this.followService.toggleFollow(true, this.entityType, this.entityName, this.entityId);
    } catch (error) {
      this.userFollowsEntity = true;
    }
  }
}
